import { useState, type ReactNode } from 'react';
import { formatLy, formatSpeed, formatEta, type FleetClient, type FleetGroupClient } from '@galaxis/shared';
import { jumpEstimate } from '../../game-time.ts';
import { useGameStore } from '../../store/game-store.ts';
import {
  fetchFleets, renameFleet, splitFleet, mergeFleets, transferShips, disbandFleet, issueTravel,
  surveyWithFleet, retreatFleet, enqueueFleetCommand, buildOutpost, bulkFleetCommand,
} from '../../api/galaxy.ts';
import { ConfirmDialog } from './ConfirmDialog.tsx';
import { FleetCommandQueue } from './FleetCommandQueue.tsx';
import { RepairPanel } from './RepairPanel.tsx';
import { RoutePlanner } from './RoutePlanner.tsx';
import {
  SHIP_CLASS_LABELS, SHIP_CLASS_ICONS, STATUS_LABELS, STATUS_COLORS, formatDuration, shipCondition, newIdempotencyKey,
} from './fleet-format.ts';
import { categoryOf, shipKinds, fleetActions, mapFocusSystem, orderProgress, CATEGORY_META, SHIP_KIND, type FleetActionId } from './fleet-category.ts';
import { CategoryBadge, useNow } from './FleetIcons.tsx';
import { GroupDialog, GroupDot } from './FleetGroups.tsx';

interface FleetDetailProps {
  fleet: FleetClient;
  fleets: FleetClient[];
  groups: FleetGroupClient[];
  onGroups: (g: FleetGroupClient[]) => void;
  onBack: () => void;
  onSelectFleet: (id: string) => void;
  /** Opens the shipyard with this fleet's system and this fleet as the destination. */
  onBuildShips?: () => void;
}

type Dialog =
  | { kind: 'split' }
  | { kind: 'pickMerge' }
  | { kind: 'merge'; sourceId: string }
  | { kind: 'pickTransfer' }
  | { kind: 'transfer'; targetId: string }
  | { kind: 'move' }
  | { kind: 'group' }
  | null;

const BTN = 'rounded-lg text-sm font-medium px-3 transition-colors disabled:opacity-40 disabled:cursor-not-allowed';
const btnStyle = (color = '#00e5ff') => ({ minHeight: 44, background: `${color}14`, color, border: `1px solid ${color}55` });

function Stat({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="bg-slate-900/60 rounded-lg px-3 py-2">
      <dt className="text-[11px] text-slate-500 uppercase tracking-wide">{label}</dt>
      <dd className="text-sm text-slate-200 mt-0.5">{children}</dd>
    </div>
  );
}

function Bar({ value, max, color }: { value: number; max: number; color: string }) {
  const pct = max > 0 ? Math.max(0, Math.min(100, (value / max) * 100)) : 0;
  return (
    <div className="h-1.5 bg-slate-800 rounded-full overflow-hidden">
      <div className="h-full rounded-full" style={{ width: `${pct}%`, background: color }} />
    </div>
  );
}

export function FleetDetail({ fleet, fleets, groups, onGroups, onBack, onSelectFleet, onBuildShips }: FleetDetailProps) {
  const now = useNow();
  const { galaxyMap, setFleets, showSystemOnMap, conflicts, me } = useGameStore();
  const [patrolRequest, setPatrolRequest] = useState(0);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [renaming, setRenaming] = useState(false);
  const [nameDraft, setNameDraft] = useState(fleet.name);
  const [splitName, setSplitName] = useState('');
  const [dialog, setDialog] = useState<Dialog>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<{ text: string; fleetId?: string } | null>(null);

  const system = (id: string | null) => (id ? galaxyMap?.systems.find(s => s.id === id) : undefined);
  const systemName = (id: string | null) => system(id)?.name ?? '—';
  const idle = fleet.status === 'idle';
  const color = STATUS_COLORS[fleet.status] ?? '#94a3b8';
  const remaining = fleet.eta ? Math.max(0, new Date(fleet.eta).getTime() - now) : null;

  const occupation = conflicts.find(c => c.attackerFleetId === fleet.id && c.status === 'occupied');
  const siblings = fleets.filter(f => f.id !== fleet.id && f.currentSystemId === fleet.currentSystemId && f.status === 'idle');
  const currentSystem = system(fleet.currentSystemId);
  const currentDiscovery = currentSystem?.discoveryStatus;
  const category = categoryOf(fleet);
  const kinds = shipKinds(fleet.stats.shipCounts);
  const neighbors = (galaxyMap?.lanes ?? [])
    .filter(l => l.fromSystemId === fleet.currentSystemId || l.toSystemId === fleet.currentSystemId)
    .map(l => system(l.fromSystemId === fleet.currentSystemId ? l.toSystemId : l.fromSystemId))
    .filter((s): s is NonNullable<typeof s> => !!s);

  async function run(action: () => Promise<unknown>, success?: string): Promise<boolean> {
    setBusy(true);
    setError(null);
    try {
      await action();
      const fresh = await fetchFleets();
      setFleets(fresh.fleets);
      if (success) setNotice({ text: success });
      return true;
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Ismeretlen hiba');
      return false;
    } finally {
      setBusy(false);
      setDialog(null);
    }
  }

  const toggle = (id: string) => setSelected(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const allSelected = fleet.ships.length > 0 && selected.size === fleet.ships.length;

  async function doRename() {
    const ok = await run(() => renameFleet(fleet.id, nameDraft.trim()), 'Flotta átnevezve.');
    if (ok) setRenaming(false);
  }

  async function doSplit() {
    const ids = [...selected];
    let created: string | undefined;
    const ok = await run(async () => {
      const res = await splitFleet(fleet.id, {
        shipIds: ids, name: splitName.trim() || undefined, idempotencyKey: newIdempotencyKey('split'),
      });
      created = res.fleets.find(f => f.id !== fleet.id)?.id;
    });
    if (ok) {
      setSelected(new Set());
      setSplitName('');
      setNotice({ text: `${ids.length} hajó új flottába került.`, fleetId: created });
    }
  }

  async function doMerge(sourceId: string) {
    const source = fleets.find(f => f.id === sourceId);
    const ok = await run(() => mergeFleets(fleet.id, { sourceFleetId: sourceId, idempotencyKey: newIdempotencyKey('merge') }));
    if (ok) setNotice({ text: `${source?.name ?? 'A flotta'} beolvadt ebbe a flottába.` });
  }

  async function doTransfer(targetId: string) {
    const ids = [...selected];
    const target = fleets.find(f => f.id === targetId);
    const ok = await run(() => transferShips(targetId, { shipIds: ids, idempotencyKey: newIdempotencyKey('transfer') }));
    if (ok) {
      setSelected(new Set());
      setNotice({ text: `${ids.length} hajó áthelyezve: ${target?.name ?? ''}`, fleetId: targetId });
    }
  }

  async function doMove(toSystemId: string) {
    await run(() => issueTravel(fleet.id, { toSystemId, idempotencyKey: newIdempotencyKey('move') }), `Indulás: ${systemName(toSystemId)}`);
  }

  /** Multi-hop route: queued as a MOVE command (the server plans the hops); with `replace` the queue is cleared first. */
  async function doRoute(toSystemId: string, replace: boolean) {
    await run(async () => {
      const res = await bulkFleetCommand({
        fleetIds: [fleet.id], command: { type: 'move', targetSystemId: toSystemId },
        idempotencyKey: newIdempotencyKey('route').slice(0, 80), replace,
      });
      const r = res.results[0];
      if (r && !r.ok) throw new Error(r.message ?? 'A parancs nem adható ki.');
    }, `Útvonal indítva: ${systemName(toSystemId)}`);
  }

  const group = groups.find(g => g.id === fleet.groupId);

  const taskLabel = fleet.status === 'traveling'
    ? `Utazás → ${systemName(fleet.destinationSystemId)}`
    : fleet.status === 'surveying' ? `Felmérés: ${systemName(fleet.currentSystemId)}`
    : fleet.status === 'constructing' ? `Építés: ${systemName(fleet.currentSystemId)}`
    : fleet.status === 'in_combat' ? 'Harcban'
    : occupation ? `Megszállás: ${systemName(occupation.systemId)}`
    : fleet.commands.automation === 'auto_survey' ? 'Automatikus felfedezés'
    : fleet.commands.automation === 'patrol' ? 'Járőrözik'
    : 'Nincs aktív parancs';

  const myCivId = me?.civilization?.id;
  const actions = fleetActions(fleet, {
    currentDiscovery,
    currentOwnedByMe: !!currentSystem?.owner && currentSystem.owner.civilizationId === myCivId,
    currentOwned: !!currentSystem?.owner,
    neighborCount: neighbors.length,
    occupying: !!occupation,
  });
  const progress = orderProgress(fleet, now);
  const editReason = idle ? null : 'Csak várakozó flotta szerkeszthető (utazás, felmérés, építés vagy harc közben nem).';

  const ACTION_COLOR: Record<FleetActionId, string> = {
    move: '#00e5ff', survey: '#38bdf8', auto_survey: '#38bdf8', outpost: '#f59e0b', station: '#f59e0b',
    attack: '#ef5350', retreat: '#f87171', patrol: '#ef5350', show_on_map: '#94a3b8',
  };
  function act(id: FleetActionId) {
    switch (id) {
      case 'move': setDialog({ kind: 'move' }); break;
      case 'survey': void run(() => surveyWithFleet(fleet.id, newIdempotencyKey('survey')), 'Felmérés elindult.'); break;
      case 'auto_survey': void run(() => enqueueFleetCommand(fleet.id, { type: 'auto_survey', idempotencyKey: newIdempotencyKey('auto') }), 'Automatikus felfedezés elindult.'); break;
      case 'outpost': void run(() => buildOutpost(fleet.currentSystemId, { systemId: fleet.currentSystemId, fleetId: fleet.id, idempotencyKey: newIdempotencyKey('outpost') }), `Előőrs építése: ${systemName(fleet.currentSystemId)}`); break;
      // A station needs a deposit choice: the system panel lists them
      case 'station': showSystemOnMap(fleet.currentSystemId); break;
      // Targets are chosen on the system panel (only fleets the player may see there)
      case 'attack': showSystemOnMap(fleet.currentSystemId); break;
      case 'retreat': void run(() => retreatFleet(fleet.id), 'Visszavonulás: a megszállás megszakítva.'); break;
      case 'patrol': setPatrolRequest(n => n + 1); break;
      case 'show_on_map': showSystemOnMap(mapFocusSystem(fleet)); break;
    }
  }

  return (
    <div className="space-y-4 pb-24" data-testid="fleet-detail" data-fleet-id={fleet.id}>
      {/* Header */}
      <div className="space-y-2">
        <button type="button" onClick={onBack} className="md:hidden text-sm text-cyan-400" style={{ minHeight: 44 }}>
          ← Flották
        </button>
        {renaming ? (
          <div className="flex gap-2">
            <input
              aria-label="Flotta neve"
              value={nameDraft}
              maxLength={40}
              onChange={e => setNameDraft(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter' && nameDraft.trim()) void doRename(); }}
              className="flex-1 min-w-0 rounded-lg bg-slate-900 border border-slate-600 px-3 text-slate-100"
              style={{ minHeight: 44 }}
              autoFocus
            />
            <button type="button" className={BTN} style={btnStyle()} disabled={busy || !nameDraft.trim()} onClick={() => void doRename()}>Mentés</button>
            <button type="button" className={BTN} style={btnStyle('#94a3b8')} onClick={() => { setRenaming(false); setNameDraft(fleet.name); }}>Mégse</button>
          </div>
        ) : (
          <div className="flex items-center justify-between gap-2">
            <h2 className="text-lg font-semibold text-slate-100 truncate">{fleet.name}</h2>
            <button type="button" className={BTN} style={btnStyle('#94a3b8')} onClick={() => { setNameDraft(fleet.name); setRenaming(true); }}>
              ✏️ Átnevezés
            </button>
          </div>
        )}
        <div className="flex flex-wrap items-center gap-2">
          <CategoryBadge category={category} kinds={kinds} reserve={fleet.role === 'reserve'} />
          <span className="inline-block text-xs px-2 py-0.5 rounded" data-testid="fleet-detail-status" style={{ color, background: `${color}15`, border: `1px solid ${color}30` }}>
            {STATUS_LABELS[fleet.status] ?? fleet.status}
          </span>
          <button type="button" data-testid="fleet-group-btn" onClick={() => setDialog({ kind: 'group' })}
            className="inline-flex items-center gap-1.5 text-xs px-2 rounded border border-slate-600 text-slate-300" style={{ minHeight: 44 }}>
            {group ? <><GroupDot color={group.color} /> {group.name}</> : '🏷 Csoport…'}
          </button>
          <span className="text-xs text-slate-500" data-testid="fleet-detail-upkeep">Fenntartás: {fleet.stats.upkeepMetalPerHour} fém/óra</span>
          {fleet.stats.damagedShips > 0 && <span className="text-xs text-amber-300" data-testid="fleet-detail-damaged">⚠ {fleet.stats.damagedShips} sérült hajó</span>}
        </div>
      </div>

      {notice && (
        <div className="rounded-lg border border-emerald-500/40 bg-emerald-500/10 px-3 py-2 text-sm text-emerald-300 flex items-center justify-between gap-2" role="status">
          <span>{notice.text}</span>
          {notice.fleetId && (
            <button type="button" className="text-emerald-200 underline" style={{ minHeight: 44 }} onClick={() => { onSelectFleet(notice.fleetId!); setNotice(null); }}>
              Megnyitás
            </button>
          )}
        </div>
      )}
      {error && <div className="rounded-lg border border-red-500/40 bg-red-500/10 px-3 py-2 text-sm text-red-300" role="alert">{error}</div>}

      {/* Composition per type (a mixed fleet keeps every type) */}
      <section aria-label="Hajóösszetétel" className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid="fleet-composition-detail">
        {Object.entries(fleet.stats.shipCounts).filter(([, n]) => n > 0).map(([cls, n]) => {
          const kindColor = CATEGORY_META[SHIP_KIND[cls] ?? 'empty'].color;
          return (
            <div key={cls} className="rounded-lg px-3 py-2 bg-slate-900/60 flex items-center justify-between gap-2" style={{ boxShadow: `inset 3px 0 0 ${kindColor}` }} data-ship-class={cls}>
              <span className="text-sm text-slate-200 truncate"><span aria-hidden>{SHIP_CLASS_ICONS[cls]}</span> {SHIP_CLASS_LABELS[cls] ?? cls}</span>
              <span className="font-mono text-sm" style={{ color: kindColor }}>×{n}</span>
            </div>
          );
        })}
      </section>

      {/* Stats */}
      <dl className="grid grid-cols-2 lg:grid-cols-3 gap-2">
        <Stat label="Helyzet">{systemName(fleet.currentSystemId)}</Stat>
        <Stat label="Cél">{fleet.destinationSystemId ? systemName(fleet.destinationSystemId) : '—'}</Stat>
        <Stat label="Hátralévő idő">{fleet.eta ? <span data-testid="fleet-eta-clock">{formatEta(fleet.eta, now)}</span> : '—'}</Stat>
        <Stat label="Sebesség"><span data-testid="fleet-speed">{formatSpeed(fleet.stats.speedLyPerHour)}</span></Stat>
        <Stat label="Katonai erő"><span className="font-mono">{fleet.stats.militaryStrength}</span> <span className="text-[11px] text-slate-500">becslés</span></Stat>
        <Stat label="Harcképes / sérült">{fleet.stats.combatReadyShips} / {fleet.stats.damagedShips}</Stat>
        <Stat label="Hajók">{fleet.stats.totalShips}</Stat>
        <Stat label="Veszteségek">{fleet.stats.losses}</Stat>
      </dl>

      {/* Command panel */}
      <section className="sci-fi-panel rounded-xl p-4 space-y-3" aria-label="Parancsok">
        <h3 className="text-xs text-slate-500 uppercase tracking-wide">Parancsok</h3>
        <div className="text-sm text-slate-200" data-testid="fleet-task">{taskLabel}</div>
        {progress !== null && remaining !== null && (
          <div className="space-y-1" data-testid="fleet-detail-eta">
            <Bar value={progress} max={1} color={color} />
            <div className="text-xs text-slate-500">Hátralévő idő: <span className="font-mono text-slate-300">{formatDuration(remaining)}</span> · {Math.round(progress * 100)}%</div>
          </div>
        )}
        <div className="text-xs text-slate-400" data-testid="fleet-detail-location">
          📍 {systemName(fleet.currentSystemId)}{fleet.destinationSystemId && <span className="text-cyan-400"> → {systemName(fleet.destinationSystemId)}</span>}
        </div>
        <h4 className="text-[11px] text-slate-500 uppercase tracking-wide pt-1">Elérhető műveletek</h4>
        <div className="grid grid-cols-2 gap-2" data-testid="fleet-actions">
          {actions.map(a => (
            <button key={a.id} type="button" className={BTN} style={btnStyle(ACTION_COLOR[a.id])} data-action={a.id}
              disabled={!!a.reason || (busy && a.id !== 'show_on_map')} title={a.reason ?? undefined} onClick={() => act(a.id)}>
              {a.label}
            </button>
          ))}
        </div>
        <ul className="text-xs text-slate-500 space-y-0.5" data-testid="fleet-action-reasons">
          {actions.filter(a => a.reason).map(a => <li key={a.id}>{a.label.replace(/^\S+\s/, '')}: {a.reason}</li>)}
          {actions.some(a => a.id === 'attack' && !a.reason) && <li>Támadás: a célpontot a rendszer paneljén választod ki (térkép).</li>}
          {actions.some(a => a.id === 'station' && !a.reason) && <li>Állomásépítés: a lelőhelyet a rendszer paneljén választod ki (térkép).</li>}
        </ul>
        {onBuildShips && fleet.currentSystemId && (
          <button type="button" className={`${BTN} w-full`} style={btnStyle('#a78bfa')} data-testid="fleet-build-ships" onClick={onBuildShips}>
            🏭 Hajó gyártása ide
          </button>
        )}
      </section>

      <RepairPanel fleet={fleet} />

      <FleetCommandQueue fleet={fleet} now={now} patrolRequest={patrolRequest} />

      {/* Organize */}
      <section className="space-y-2">
        <div className="flex items-center justify-between">
          <h3 className="text-xs text-slate-500 uppercase tracking-wide">Hajók ({fleet.ships.length})</h3>
          {idle && fleet.ships.length > 0 && (
            <button type="button" className="text-xs text-cyan-400" style={{ minHeight: 44 }}
              onClick={() => setSelected(allSelected ? new Set() : new Set(fleet.ships.map(s => s.id)))}>
              {allSelected ? 'Kijelölés törlése' : 'Összes kijelölése'}
            </button>
          )}
        </div>
        {editReason && <p className="text-xs text-amber-300/80">{editReason}</p>}
        <ul className="space-y-1.5">
          {fleet.ships.map(ship => {
            const cond = shipCondition(ship.currentHull ?? 0, ship.maxHull ?? 0);
            return (
              <li key={ship.id}>
                <label className="flex items-center gap-3 bg-slate-900/60 rounded-lg px-3 py-2 cursor-pointer" style={{ minHeight: 52 }}>
                  <input
                    type="checkbox"
                    aria-label={ship.name}
                    className="w-5 h-5 accent-cyan-400 flex-shrink-0"
                    checked={selected.has(ship.id)}
                    disabled={!idle}
                    onChange={() => toggle(ship.id)}
                  />
                  <span className="text-lg flex-shrink-0" aria-hidden>{SHIP_CLASS_ICONS[ship.class]}</span>
                  <span className="flex-1 min-w-0 space-y-1">
                    <span className="flex items-center justify-between gap-2">
                      <span className="text-sm text-slate-200 truncate">{ship.name}</span>
                      <span className="text-[11px] flex-shrink-0" style={{ color: cond.color }}>{cond.label}</span>
                    </span>
                    <span className="block text-[11px] text-slate-500">{SHIP_CLASS_LABELS[ship.class]} · {ship.currentHull}/{ship.maxHull} test · {ship.currentShield}/{ship.maxShield} pajzs</span>
                    <Bar value={ship.currentHull ?? 0} max={ship.maxHull ?? 0} color={cond.color} />
                  </span>
                </label>
              </li>
            );
          })}
        </ul>
        {fleet.ships.length === 0 && (
          <button type="button" className={`${BTN} w-full`} style={btnStyle('#f87171')} disabled={busy || !idle}
            onClick={() => void run(() => disbandFleet(fleet.id)).then(ok => { if (ok) onBack(); })}>
            Üres flotta megszüntetése
          </button>
        )}
        <button type="button" className={`${BTN} w-full`} style={btnStyle('#a78bfa')} disabled={!idle || busy || siblings.length === 0}
          title={siblings.length === 0 ? 'Nincs másik várakozó flotta ebben a rendszerben.' : undefined}
          onClick={() => setDialog({ kind: 'pickMerge' })}>
          🔗 Egyesítés másik flottával…
        </button>
        {idle && siblings.length === 0 && <p className="text-xs text-slate-500">Egyesítéshez egy másik várakozó flottának ugyanebben a rendszerben kell lennie.</p>}
      </section>

      {/* Selection action bar */}
      {selected.size > 0 && (
        <div className="fixed md:sticky bottom-[var(--guide-h,0px)] md:bottom-0 left-0 right-0 z-40 p-3 pb-[calc(0.75rem+var(--sab))] md:pb-3 md:px-0 bg-slate-950/95 md:bg-transparent border-t md:border-0 border-slate-700">
          <div className="sci-fi-panel rounded-xl p-3 flex flex-wrap items-center gap-2" role="toolbar" aria-label="Kijelölt hajók műveletei">
            <span className="text-sm text-slate-300 flex-1 min-w-[8rem]">{selected.size} hajó kijelölve</span>
            <button type="button" className={BTN} style={btnStyle()} disabled={busy || allSelected}
              title={allSelected ? 'Legalább egy hajónak maradnia kell — helyette nevezd át a flottát.' : undefined}
              onClick={() => setDialog({ kind: 'split' })}>
              ✂️ Új flotta
            </button>
            <button type="button" className={BTN} style={btnStyle('#a78bfa')} disabled={busy || siblings.length === 0}
              onClick={() => setDialog({ kind: 'pickTransfer' })}>
              ↪ Áthelyezés…
            </button>
          </div>
        </div>
      )}

      {/* Dialogs */}
      {dialog?.kind === 'split' && (
        <ConfirmDialog title="Flotta szétválasztása" confirmLabel="Szétválasztás" busy={busy} onCancel={() => setDialog(null)} onConfirm={() => void doSplit()}>
          <p>{selected.size} hajó új flottába kerül itt: <strong>{systemName(fleet.currentSystemId)}</strong>.</p>
          <label className="block text-xs text-slate-400">
            Új flotta neve (nem kötelező)
            <input
              aria-label="Új flotta neve"
              value={splitName}
              maxLength={40}
              onChange={e => setSplitName(e.target.value)}
              className="mt-1 w-full rounded-lg bg-slate-900 border border-slate-600 px-3 text-slate-100"
              style={{ minHeight: 44 }}
            />
          </label>
        </ConfirmDialog>
      )}
      {(dialog?.kind === 'pickMerge' || dialog?.kind === 'pickTransfer') && (
        <ConfirmDialog
          title={dialog.kind === 'pickMerge' ? 'Melyik flottát olvasztod ide?' : 'Melyik flottába kerüljenek?'}
          confirmLabel="Bezárás"
          onCancel={() => setDialog(null)}
          onConfirm={() => setDialog(null)}
        >
          <ul className="space-y-2">
            {siblings.map(s => (
              <li key={s.id}>
                <button type="button" className={`${BTN} w-full text-left`} style={btnStyle('#a78bfa')}
                  onClick={() => setDialog(dialog.kind === 'pickMerge' ? { kind: 'merge', sourceId: s.id } : { kind: 'transfer', targetId: s.id })}>
                  {s.name} — {s.ships.length} hajó
                </button>
              </li>
            ))}
          </ul>
        </ConfirmDialog>
      )}
      {dialog?.kind === 'merge' && (
        <ConfirmDialog title="Flották egyesítése" confirmLabel="Egyesítés" busy={busy} onCancel={() => setDialog(null)} onConfirm={() => void doMerge(dialog.sourceId)}>
          <p><strong>{fleets.find(f => f.id === dialog.sourceId)?.name}</strong> összes hajója átkerül ide: <strong>{fleet.name}</strong>.</p>
          <p className="text-xs text-slate-400">A kiürült flotta megszűnik. A csatatörténet megmarad.</p>
        </ConfirmDialog>
      )}
      {dialog?.kind === 'transfer' && (
        <ConfirmDialog title="Hajók áthelyezése" confirmLabel="Áthelyezés" busy={busy} onCancel={() => setDialog(null)} onConfirm={() => void doTransfer(dialog.targetId)}>
          <p>{selected.size} hajó átkerül ide: <strong>{fleets.find(f => f.id === dialog.targetId)?.name}</strong>.</p>
        </ConfirmDialog>
      )}
      {dialog?.kind === 'group' && (
        <GroupDialog mode="assign" groups={groups} onGroups={onGroups} fleetIds={[fleet.id]} onClose={() => setDialog(null)} />
      )}
      {dialog?.kind === 'move' && (
        <ConfirmDialog title="Hová induljon a flotta?" confirmLabel="Bezárás" onCancel={() => setDialog(null)} onConfirm={() => setDialog(null)}>
          <ul className="space-y-2">
            {neighbors.map(n => {
              const jump = jumpEstimate(fleet, galaxyMap, n.id);
              return (
              <li key={n.id}>
                <button type="button" className={`${BTN} w-full text-left flex justify-between items-center`} style={btnStyle()} disabled={busy}
                  onClick={() => void doMove(n.id)}>
                  <span>{n.name}</span>
                  <span className="text-[11px] text-slate-400 text-right" data-testid="move-jump">
                    {jump && <span className="block font-mono text-cyan-300">{formatLy(jump.distance)} · {formatDuration(jump.durationMs)}</span>}
                    {n.owner ? n.owner.civilizationName : n.discoveryStatus === 'detected' ? 'Meglátogatva' : 'Felmért'}
                  </span>
                </button>
              </li>
              );
            })}
          </ul>
          <p className="text-xs text-slate-500">Sebesség: {formatSpeed(fleet.stats.speedLyPerHour)} (a leglassabb hajó szerint). Az idő a sáv hosszától függ.</p>
          <hr className="border-slate-700" />
          <RoutePlanner fleet={fleet} map={galaxyMap} busy={busy} onGo={(t, replace) => void doRoute(t, replace)} />
        </ConfirmDialog>
      )}
    </div>
  );
}
